import { Grid, Skeleton, Typography } from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { ElementItemProps } from "../types";
import { useDevice } from "../../../../utils/DeviceProvider";
import { ChkboxElement } from "./Chkbox";
import { TxtboxElement } from "./Txtbox";
import Info from "./Info";
import { useResetContextMenu } from "../../../../utils/ResetContextMenuProvider";

interface OBUTV123Parameters {
  version: number;
  index: number;
}

interface OBUTV1Value {
  enabled: boolean;
  event: string;
  zone: string;
}

interface OBUTV2Value extends OBUTV1Value {
  phone: string;
}

interface OBUTV3Value extends OBUTV2Value {
  name: string;
}

const OBUTV123 = ({ version, configKey, index, disabled }: Omit<ElementItemProps<OBUTV123Parameters>, "elementKey" | "label">) => {
  const [active] = useState({ active: true });
  const [configIndex, setConfigIndex] = useState<number | null>(null);
  const [saved, setSaved] = useState<OBUTV1Value | OBUTV2Value | OBUTV3Value | null>(null);
  const [enabled, setEnabled] = useState<boolean | null>(null);
  const [event, setEvent] = useState("");
  const [zone, setZone] = useState("");
  const [phone, setPhone] = useState("");
  const [name, setName] = useState("");

  const { formatMessage } = useIntl();
  const { openContextMenu } = useResetContextMenu();

  const {
    device: { currentValue, config },
    setCurrentValue,
  } = useDevice();

  const onChange = useCallback(
    ({ enabled, event, zone, phone, name }: { enabled?: boolean; event?: string; zone?: string; phone?: string; name?: string }) => {
      if (configIndex === null) {
        return;
      }
      if (!currentValue[configKey].items[configIndex].value) {
        currentValue[configKey].items[configIndex].value = {};
      }
      if (enabled !== undefined) {
        currentValue[configKey].items[configIndex].value.enabled = enabled;
      }
      if (event !== undefined) {
        const eventValue = event.padStart(4, "0");
        if (event !== eventValue) {
          setEvent(eventValue);
        }
        currentValue[configKey].items[configIndex].value.event = eventValue;
      }
      if (zone !== undefined) {
        const zoneValue = zone.padStart(3, "0");
        if (zone !== zoneValue) {
          setZone(zoneValue);
        }
        currentValue[configKey].items[configIndex].value.zone = zoneValue;
      }
      if (phone !== undefined) {
        currentValue[configKey].items[configIndex].value.phone = phone;
      }
      if (name !== undefined) {
        currentValue[configKey].items[configIndex].value.name = name;
      }
      setCurrentValue(currentValue);
    },
    [setCurrentValue, currentValue, configKey, configIndex]
  );

  const onEnabledChange = useCallback(
    (value: boolean) => {
      setEnabled(value);
      onChange({ enabled: value });
    },
    [onChange]
  );

  const resetValue = useCallback(
    (key: string) => {
      if (!config || configIndex === null) {
        return;
      }
      const saved = config.toolboxConfig[configKey].items[configIndex].value ?? {};
      switch (key) {
        case "enabled":
          currentValue[configKey].items[configIndex].value.enabled = saved.enabled ?? false;
          break;
        case "event":
        case "zone":
        case "phone":
        case "name":
          currentValue[configKey].items[configIndex].value[key] = saved[key] ?? "";
          break;
      }
      setCurrentValue(currentValue);
    },
    [config, configIndex, configKey, currentValue, setCurrentValue]
  );

  useEffect(() => {
    active.active = true;

    return () => {
      active.active = false;
    };
  }, [active]);

  useEffect(() => {
    if (!currentValue) {
      return;
    }
    const cIndex = currentValue[configKey]?.items?.findIndex(
      ({ type, key }: { type: string; key: string }) => type === `OBUTV${version}` && key === `OBUTV${version}-${index}`
    ) ?? null;
    setConfigIndex(cIndex);
  }, [configKey, currentValue, version, index]);

  useEffect(() => {
    if (!currentValue || !config || configIndex === null || configIndex === -1) {
      return;
    }
    const svd = config.toolboxConfig[configKey]?.items?.[configIndex].value ?? null;
    setSaved(svd);
    const { enabled, event, zone, phone, name } = currentValue[configKey].items[configIndex]?.value ?? {};
    setEnabled(enabled);
    setEvent(event);
    setZone(zone);
    if (phone !== undefined) {
      setPhone(phone);
    }
    if (name !== undefined) {
      setName(name);
    }
  }, [active, currentValue, config, configIndex, configKey]);

  if (enabled === null) {
    return (
      <Grid container spacing={1}>
        <Grid item>
          <Info text={`${index + 1}.`} inlineTitle />
        </Grid>
        <Grid item>
          <Skeleton variant="rectangular" height={40} sx={{ width: 200, borderRadius: 1 }} />
        </Grid>
        <Grid item>
          <Skeleton variant="rectangular" height={40} sx={{ width: 200, borderRadius: 1 }} />
        </Grid>
        {version > 1 ? (
          <Grid item>
            <Skeleton variant="rectangular" height={40} sx={{ width: 200, borderRadius: 1 }} />
          </Grid>
        ) : null}
        {version > 2 ? (
          <Grid item>
            <Skeleton variant="rectangular" height={40} sx={{ width: 200, borderRadius: 1 }} />
          </Grid>
        ) : null}
      </Grid>
    );
  }

  if (configIndex === -1) {
    return <Typography>{formatMessage({ defaultMessage: "Config not found." })}</Typography>;
  }

  return (
    <Grid container spacing={1}>
      <Grid item>
        <Info text={`${index + 1}.`} inlineTitle />
      </Grid>
      <Grid item>
        <ChkboxElement
          value={enabled}
          label={formatMessage({ defaultMessage: "Enabled" })}
          onChange={onEnabledChange}
          edited={saved?.enabled !== enabled}
          disabled={disabled}
          onContextMenu={
            saved && saved.enabled !== enabled
              ? (e) => openContextMenu(e, `${configKey}.items.${configIndex}.value.enabled`, { onReset: () => resetValue("enabled") })
              : undefined
          }
        />
      </Grid>
      <Grid item>
        <TxtboxElement
          value={event ?? ""}
          label={formatMessage({ defaultMessage: "Event" })}
          onChange={setEvent}
          onBlur={() => onChange({ event })}
          edited={saved?.event !== event}
          hex
          maxLength={4}
          disabled={disabled}
          onContextMenu={
            saved && saved.event !== event
              ? (e) => openContextMenu(e, `${configKey}.items.${configIndex}.value.event`, { onReset: () => resetValue("event") })
              : undefined
          }
        />
      </Grid>
      <Grid item>
        <TxtboxElement
          value={zone ?? ""}
          label={formatMessage({ defaultMessage: "Zone" })}
          onChange={setZone}
          onBlur={() => onChange({ zone })}
          edited={saved?.zone !== zone}
          hex
          maxLength={3}
          disabled={disabled}
          onContextMenu={
            saved && saved.zone !== zone
              ? (e) => openContextMenu(e, `${configKey}.items.${configIndex}.value.zone`, { onReset: () => resetValue("zone") })
              : undefined
          }
        />
      </Grid>
      {version > 1 ? (
        <Grid item>
          <TxtboxElement
            value={phone ?? ""}
            label={formatMessage({ defaultMessage: "Phone number" })}
            onChange={setPhone}
            onBlur={() => onChange({ phone })}
            edited={((saved as OBUTV2Value)?.phone ?? "") !== phone}
            disabled={disabled}
            onContextMenu={
              saved && (saved as OBUTV2Value).phone !== phone
                ? (e) => openContextMenu(e, `${configKey}.items.${configIndex}.value.phone`, { onReset: () => resetValue("phone") })
                : undefined
            }
          />
        </Grid>
      ) : null}
      {version > 2 ? (
        <Grid item>
          <TxtboxElement
            value={name ?? ""}
            label={formatMessage({ defaultMessage: "Name" })}
            onChange={setName}
            onBlur={() => onChange({ name })}
            edited={((saved as OBUTV3Value)?.name ?? "") !== name}
            disabled={disabled}
            onContextMenu={
              saved && (saved as OBUTV3Value).name !== name
                ? (e) => openContextMenu(e, `${configKey}.items.${configIndex}.value.name`, { onReset: () => resetValue("name") })
                : undefined
            }
          />
        </Grid>
      ) : null}
    </Grid>
  );
};

export default OBUTV123;
